import React from 'react';
import { useTranslation } from 'react-i18next';
import { colors } from '@guryou/colors';
import { HeroImage } from '@guryou/html-components';

const HeroSection = () => {
  const { t } = useTranslation();

  return (
    <section
      className="relative overflow-hidden min-h-screen"
      style={{
        background: `linear-gradient(135deg, ${colors.mineShaft} 0%, ${colors.brownMineShaft} 50%, ${colors.mineShaft} 100%)`,
      }}
    >
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20" style={{ backgroundImage: `url(${HeroImage})` }}></div>
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(to right, ${colors.mineShaft}CC, ${colors.brownMineShaft}99, ${colors.mineShaft}CC)`,
          }}
        ></div>
      </div>

      {/* Content */}
      <div className="relative z-10 px-4 mx-auto max-w-screen-xl min-h-screen flex items-center">
        <div className="w-full">
          {/* Main Content Container */}
          <div className="grid gap-6 sm:gap-8 lg:grid-cols-12 lg:gap-16 items-center w-full">
            {/* Left Column - Text Content */}
            <div className="mr-auto place-self-center lg:col-span-7">
              {/* Announcement Badge */}
              <div
                className="inline-flex items-center justify-between p-1 mb-4 sm:mb-6 lg:mb-7 text-xs sm:text-sm rounded-lg w-full sm:w-auto"
                style={{
                  backgroundColor: colors.brownMineShaft,
                  borderColor: colors.mercury,
                  color: colors.white,
                }}
              >
                <span
                  className="inline-flex items-center px-2 py-0.5 sm:px-2.5 rounded text-xs font-medium text-white mr-2 sm:mr-3"
                  style={{ backgroundColor: 'var(--feel-primary)' }}
                >
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path
                      fillRule="evenodd"
                      d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                  New
                </span>
                <span className="text-xs sm:text-sm font-medium flex-1 text-center sm:text-left">{t('HERO_SECTION.newTag')}</span>
              </div>

              {/* Main Heading */}
              <h1 className="max-w-2xl mb-3 sm:mb-4 text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold leading-tight sm:leading-none tracking-tight text-white">
                {t('HERO_SECTION.title')}
              </h1>

              {/* Description */}
              <p
                className="max-w-2xl mb-4 sm:mb-6 lg:mb-8 text-sm sm:text-base md:text-lg lg:text-xl font-light leading-relaxed"
                style={{ color: colors.mercury }}
              >
                {t('HERO_SECTION.description')}
              </p>

              {/* Action Buttons */}
              <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-4">
                <button
                  className={`inline-flex items-center justify-center px-4 py-2.5 sm:px-5 sm:py-3 text-sm sm:text-base font-medium text-center text-white rounded-lg focus:ring-4 focus:ring-gray-300 transition-all duration-200 hover:scale-105 w-full sm:w-auto bg-[var(--feel-primary)] hover:bg-[var(--feel-primary-dark)]`}
                  style={{
                    '--focus-ring-color': colors.cabaret,
                  }}
                >
                  {t('HERO_SECTION.getStarted')}
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 ml-2 -mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path
                      fillRule="evenodd"
                      d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                </button>

                <button
                  className={`inline-flex items-center justify-center px-4 py-2.5 sm:px-5 sm:py-3 text-sm sm:text-base font-medium text-center text-white border border-gray-300 rounded-lg hover:bg-gray-100 hover:text-gray-900 focus:ring-4 focus:ring-gray-100 transition-all duration-200 w-full sm:w-auto border-[${colors.mercury}] hover:bg-[${colors.mercury}] hover:text-[${colors.mineShaft}]`}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 -ml-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z"></path>
                  </svg>
                  {t('HERO_SECTION.watchVideo')}
                </button>
              </div>
            </div>

            {/* Right Column - Statistics Cards (Desktop Only) */}
            <div className="hidden lg:mt-0 lg:col-span-5 lg:flex">
              <div className="grid grid-cols-1 gap-6 w-full">
                {/* Statistics Card 1 */}
                <div
                  className={`backdrop-blur-sm rounded-lg p-6 text-center transition-all duration-300 hover:scale-105 border border-[${colors.mercury}]`}
                  style={{
                    backgroundColor: `${colors.brownMineShaft}40`,
                  }}
                >
                  <div className="text-3xl font-bold text-white mb-2">73M+</div>
                  <div className="text-sm font-medium" style={{ color: 'var(--feel-primary)' }}>
                    {t('HERO_SECTION.statistics.centers')}
                  </div>
                </div>

                {/* Statistics Card 2 */}
                <div
                  className={`backdrop-blur-sm rounded-lg p-6 text-center transition-all duration-300 hover:scale-105 border border-[${colors.mercury}]`}
                  style={{
                    backgroundColor: `${colors.brownMineShaft}40`,
                  }}
                >
                  <div className="text-3xl font-bold text-white mb-2">1B+</div>
                  <div className="text-sm font-medium" style={{ color: 'var(--feel-primary)' }}>
                    {t('HERO_SECTION.statistics.professionals')}
                  </div>
                </div>

                {/* Statistics Card 3 */}
                <div
                  className={`backdrop-blur-sm rounded-lg p-6 text-center transition-all duration-300 hover:scale-105 border border-[${colors.mercury}]`}
                  style={{
                    backgroundColor: `${colors.brownMineShaft}40`,
                  }}
                >
                  <div className="text-3xl font-bold text-white mb-2">4M+</div>
                  <div className="text-sm font-medium" style={{ color: 'var(--feel-primary)' }}>
                    {t('HERO_SECTION.statistics.users')}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Statistics Section - Below Main Content */}
          <div className="mt-8 sm:mt-12 md:mt-16 lg:mt-16">
            <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4 lg:hidden">
              <div
                className="backdrop-blur-sm rounded-lg p-2 sm:p-3 md:p-4 text-center"
                style={{
                  backgroundColor: `${colors.brownMineShaft}40`,
                  borderColor: colors.mercury,
                  borderWidth: '1px',
                }}
              >
                <div className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-1">73M+</div>
                <div className="text-xs sm:text-sm font-medium" style={{ color: 'var(--feel-primary)' }}>
                  {t('HERO_SECTION.statistics.centers')}
                </div>
              </div>
              <div
                className="backdrop-blur-sm rounded-lg p-2 sm:p-3 md:p-4 text-center"
                style={{
                  backgroundColor: `${colors.brownMineShaft}40`,
                  borderColor: colors.mercury,
                  borderWidth: '1px',
                }}
              >
                <div className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-1">1B+</div>
                <div className="text-xs sm:text-sm font-medium" style={{ color: 'var(--feel-primary)' }}>
                  {t('HERO_SECTION.statistics.professionals')}
                </div>
              </div>
              <div
                className="backdrop-blur-sm rounded-lg p-2 sm:p-3 md:p-4 text-center"
                style={{
                  backgroundColor: `${colors.brownMineShaft}40`,
                  borderColor: colors.mercury,
                  borderWidth: '1px',
                }}
              >
                <div className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-1">4M+</div>
                <div className="text-xs sm:text-sm font-medium" style={{ color: 'var(--feel-primary)' }}>
                  {t('HERO_SECTION.statistics.users')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
